// Animated Self Discovery statistics — inspirational colorful viz wired to real mood/signal data.
// SVG + CSS only (no chart library). Interactive: hover peaks, tap spectrum rows, breathing radar.

import { useId, useMemo, useState } from 'react';
import type { DayWeather, MoodKey, MoodWeather, RisingSignal } from '../api/mood';

// ─── shared geometry ───────────────────────────────────────────

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

/** Map -1..1 valence to 0..1 chart height (higher = brighter). */
export function valenceNorm(v: number | null | undefined): number | null {
  if (v == null || Number.isNaN(v)) return null;
  return clamp01((v + 1) / 2);
}

/** Smooth open path through points (Catmull-Rom → cubic Bezier). */
export function smoothPath(points: { x: number; y: number }[], tension = 0.2): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function shortDay(day: string): string {
  return day.slice(5); // MM-DD
}

function moodWord(key: MoodKey | null, fallback: string): string {
  if (!key) return fallback;
  return key.charAt(0).toUpperCase() + key.slice(1);
}

// ─── 1. Emotional Shift Tracker (gradient area) ────────────────

export function EmotionalShiftChart({ weather }: { weather: DayWeather[] }) {
  const uid = useId().replace(/:/g, '');
  const [active, setActive] = useState<number | null>(null);
  const days = weather.slice(-10);

  const { points, area, peaks, labels } = useMemo(() => {
    const W = 640;
    const H = 168;
    const padX = 24;
    const padTop = 28;
    const padBot = 26;
    const usable = days.length > 1 ? days.length - 1 : 1;

    const pts = days.map((d, i) => {
      const self = valenceNorm(d.selfValence);
      const journal = valenceNorm(d.journalValence);
      // Prefer journals; if neither side has data, sit mid-low so empty weeks don't fake a high flatline.
      let n: number;
      if (journal != null && self != null) n = journal * 0.65 + self * 0.35;
      else if (journal != null) n = journal;
      else if (self != null) n = self;
      else n = 0.28;
      const x = padX + (i / usable) * (W - padX * 2);
      const y = padTop + (1 - n) * (H - padTop - padBot);
      return { x, y, n, day: d };
    });

    const line = smoothPath(pts);
    const areaPath =
      pts.length > 0
        ? `${line} L ${pts[pts.length - 1].x} ${H - 6} L ${pts[0].x} ${H - 6} Z`
        : '';

    // Annotate local maxima / interesting days (up to 4).
    const scored = pts
      .map((p, i) => {
        const prev = pts[i - 1]?.n ?? p.n;
        const next = pts[i + 1]?.n ?? p.n;
        const hasSignal = p.day.sampleCount > 0 || p.day.selfMood;
        const isPeak = hasSignal && p.n >= prev && p.n >= next;
        const emotion = p.day.topEmotions[0] || moodWord(p.day.selfMood, '');
        return { i, isPeak, emotion, n: p.n, x: p.x, y: p.y };
      })
      .filter((p) => p.isPeak && p.emotion)
      .sort((a, b) => b.n - a.n)
      .slice(0, 4)
      .sort((a, b) => a.i - b.i);

    return {
      points: pts,
      area: areaPath,
      peaks: scored,
      labels: days.map((d) => shortDay(d.day)),
      W,
      H,
    };
  }, [days]);

  if (days.length === 0) {
    return <p className="obody" style={{ margin: 0 }}>Check in and write — the shift chart fills as signals arrive.</p>;
  }

  const focus = active != null ? points[active] : null;
  const H = 168;

  return (
    <div className="ostat-shift">
      <svg
        className="ostat-shift-svg"
        viewBox={`0 0 640 ${H}`}
        role="img"
        aria-label="Emotional shift over recent days"
        onMouseLeave={() => setActive(null)}
      >
        <defs>
          <linearGradient id={`oshift-fill-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7eb8f0" />
            <stop offset="28%" stopColor="#b8a0e8" />
            <stop offset="55%" stopColor="#f0a0c0" />
            <stop offset="78%" stopColor="#f5b878" />
            <stop offset="100%" stopColor="#c8e070" />
          </linearGradient>
          <linearGradient id={`oshift-fade-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          <mask id={`oshift-mask-${uid}`}>
            <rect width="640" height={H} fill={`url(#oshift-fade-${uid})`} />
          </mask>
        </defs>

        {peaks.map((p) => (
          <line
            key={`g-${p.i}`}
            className="ostat-shift-guide"
            x1={p.x}
            y1={16}
            x2={p.x}
            y2={p.y}
          />
        ))}

        <path
          className="ostat-shift-area"
          d={area}
          fill={`url(#oshift-fill-${uid})`}
          mask={`url(#oshift-mask-${uid})`}
        />
        <path className="ostat-shift-line" d={smoothPath(points)} fill="none" />

        {peaks.map((p) => (
          <text
            key={`t-${p.i}`}
            className="ostat-shift-annote"
            x={p.x}
            y={14}
            textAnchor="middle"
            fontSize={10}
          >
            {p.emotion}
          </text>
        ))}

        {points.map((p, i) => (
          <g key={p.day.day}>
            <circle
              className={`ostat-shift-dot${active === i ? ' is-active' : ''}`}
              cx={p.x}
              cy={p.y}
              r={active === i ? 6 : 4}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              tabIndex={0}
              role="button"
              aria-label={`${shortDay(p.day.day)}: ${moodWord(p.day.selfMood, 'no check-in')}, journals ${
                p.day.journalValence != null ? p.day.journalValence.toFixed(2) : 'quiet'
              }`}
            />
            <text
              className="ostat-shift-day"
              x={p.x}
              y={H - 6}
              textAnchor="middle"
              fontSize={9}
            >
              {labels[i]}
            </text>
          </g>
        ))}
      </svg>

      {focus ? (
        <div className="ostat-shift-tip" aria-live="polite">
          <strong>{shortDay(focus.day.day)}</strong>
          {focus.day.selfMood ? ` · you: ${focus.day.selfMood}` : ' · no check-in'}
          {focus.day.journalValence != null
            ? ` · journals: ${focus.day.journalValence.toFixed(2)}`
            : ' · journals quiet'}
          {focus.day.topEmotions[0] ? ` · ${focus.day.topEmotions.join(', ')}` : ''}
        </div>
      ) : (
        <div className="ostat-shift-tip mute">Hover a point for that day’s self-check and journal tone.</div>
      )}
    </div>
  );
}

// ─── 2. Mood spectrum path (self vs journals) ──────────────────

const SPECTRA: { id: string; left: string; right: string; fromMood: (k: MoodKey | null, j: number | null) => number }[] = [
  {
    id: 'weight',
    left: 'Heavy',
    right: 'Bright',
    fromMood: (k, j) => {
      if (k === 'heavy' || k === 'raw') return 0.15;
      if (k === 'bright' || k === 'calm') return 0.85;
      if (j != null) return valenceNorm(j) ?? 0.5;
      return 0.5;
    },
  },
  {
    id: 'tempo',
    left: 'Restless',
    right: 'Steady',
    fromMood: (k, j) => {
      if (k === 'restless') return 0.18;
      if (k === 'steady' || k === 'calm') return 0.82;
      if (j != null) return 0.35 + (valenceNorm(j) ?? 0.5) * 0.3;
      return 0.5;
    },
  },
  {
    id: 'texture',
    left: 'Raw',
    right: 'Tender',
    fromMood: (k) => {
      if (k === 'raw') return 0.2;
      if (k === 'tender') return 0.8;
      if (k === 'heavy') return 0.3;
      if (k === 'bright') return 0.65;
      return 0.5;
    },
  },
  {
    id: 'depth',
    left: 'Storm',
    right: 'Clear',
    fromMood: (_k, j) => valenceNorm(j) ?? 0.48,
  },
  {
    id: 'energy',
    left: 'Drained',
    right: 'Alive',
    fromMood: (k, j) => {
      if (k === 'bright') return 0.88;
      if (k === 'heavy' || k === 'raw') return 0.22;
      if (j != null) return valenceNorm(j) ?? 0.5;
      return 0.5;
    },
  },
];

export function MoodSpectrumPath({
  todayMood,
  journalValence,
}: {
  todayMood: MoodKey | null;
  journalValence: number | null;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const W = 560;
  const rowH = 44;
  const padX = 88;
  const trackW = W - padX * 2;

  const rows = SPECTRA.map((s, i) => {
    const selfT = s.fromMood(todayMood, null);
    // Journal path uses journal valence where meaningful; texture spectrum stays self-leaning.
    const journalT =
      s.id === 'texture'
        ? s.fromMood(null, journalValence)
        : s.fromMood(null, journalValence);
    return {
      ...s,
      i,
      y: 28 + i * rowH,
      selfX: padX + selfT * trackW,
      journalX: padX + journalT * trackW,
    };
  });

  const selfPath = smoothPath(rows.map((r) => ({ x: r.selfX, y: r.y })), 0.35);
  const journalPath = smoothPath(rows.map((r) => ({ x: r.journalX, y: r.y })), 0.35);
  const H = 28 + SPECTRA.length * rowH;

  return (
    <div className="ostat-spectrum">
      <svg
        className="ostat-spectrum-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Mood spectrums: what you named versus what journals reveal"
      >
        <defs>
          <filter id="ospec-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Glow paths under sharp strokes */}
        <path className="ostat-spectrum-glow journal" d={journalPath} filter="url(#ospec-glow)" />
        <path className="ostat-spectrum-glow self" d={selfPath} filter="url(#ospec-glow)" />
        <path className="ostat-spectrum-path journal" d={journalPath} />
        <path className="ostat-spectrum-path self" d={selfPath} />

        {rows.map((r) => (
          <g
            key={r.id}
            className={`ostat-spectrum-row${hover === r.id ? ' is-hot' : ''}`}
            onMouseEnter={() => setHover(r.id)}
            onMouseLeave={() => setHover(null)}
          >
            <line
              className="ostat-spectrum-track"
              x1={padX}
              y1={r.y}
              x2={padX + trackW}
              y2={r.y}
            />
            {/* Distribution ticks */}
            {[0.2, 0.35, 0.5, 0.65, 0.8].map((t) => (
              <circle
                key={t}
                className="ostat-spectrum-tick"
                cx={padX + t * trackW}
                cy={r.y}
                r={2.2}
              />
            ))}
            <text className="ostat-spectrum-label left" x={padX - 12} y={r.y + 4} textAnchor="end">
              {r.left}
            </text>
            <text className="ostat-spectrum-label right" x={padX + trackW + 12} y={r.y + 4} textAnchor="start">
              {r.right}
            </text>
            <circle className="ostat-spectrum-node journal" cx={r.journalX} cy={r.y} r={6} />
            <circle className="ostat-spectrum-node self" cx={r.selfX} cy={r.y} r={5} />
          </g>
        ))}
      </svg>
      <div className="ostat-spectrum-legend">
        <span>
          <i className="omood-swatch self" /> You said
        </span>
        <span>
          <i className="omood-swatch journal" /> Journals reveal
        </span>
      </div>
    </div>
  );
}

// ─── 3. Mood presence mandala ──────────────────────────────────

export function MoodPresenceMandala({
  label,
  percent,
  subtitle,
}: {
  label: string;
  percent: number;
  subtitle: string;
}) {
  const pct = Math.round(clamp01(percent / 100) * 100);
  return (
    <div className="ostat-mandala" role="img" aria-label={`${label} ${pct}%`}>
      <div className="ostat-mandala-orb" aria-hidden>
        {Array.from({ length: 8 }, (_, i) => (
          <span
            key={i}
            className="ostat-mandala-petal"
            style={{ ['--petal-a' as string]: `${i * 22.5}deg` }}
          />
        ))}
        <div className="ostat-mandala-core">
          <span className="ostat-mandala-label">{label}</span>
          <span className="ostat-mandala-pct">{pct}%</span>
        </div>
      </div>
      <p className="ostat-mandala-sub">{subtitle}</p>
    </div>
  );
}

// ─── 4. Signal radar (organic blobs) ───────────────────────────

const RADAR_AXES = ['Emotion', 'Theme', 'Body', 'Symbol', 'Pattern'] as const;

function scoresFromSignals(signals: RisingSignal[]): number[] {
  const byKind: Record<string, number> = {
    emotion: 0,
    theme: 0,
    body: 0,
    symbol: 0,
    pattern: 0,
  };
  let max = 1;
  for (const s of signals) {
    const k = s.kind in byKind ? s.kind : 'pattern';
    byKind[k] = (byKind[k] ?? 0) + s.count;
    max = Math.max(max, byKind[k]);
  }
  // Soft floor so empty axes still show a whisper of shape.
  return RADAR_AXES.map((axis) => {
    const key = axis.toLowerCase();
    const v = byKind[key] ?? 0;
    return 0.18 + (v / max) * 0.72;
  });
}

function radarPolygon(scores: number[], cx: number, cy: number, r: number): string {
  const n = scores.length;
  return scores
    .map((s, i) => {
      const a = -Math.PI / 2 + (i / n) * Math.PI * 2;
      const x = cx + Math.cos(a) * r * s;
      const y = cy + Math.sin(a) * r * s;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ') + ' Z';
}

export function SignalRadar({
  signals,
  previous,
}: {
  signals: RisingSignal[];
  /** Optional prior window scores 0..1 — when absent, derive a quieter ghost from current. */
  previous?: number[];
}) {
  const [active, setActive] = useState<number | null>(null);
  const now = scoresFromSignals(signals);
  const was = previous ?? now.map((s) => Math.max(0.15, s * 0.72));
  const cx = 160;
  const cy = 160;
  const R = 110;

  const nowPath = radarPolygon(now, cx, cy, R);
  const wasPath = radarPolygon(was, cx, cy, R);

  return (
    <div className="ostat-radar">
      <svg
        className="ostat-radar-svg"
        viewBox="0 0 320 320"
        role="img"
        aria-label="Signal radar across emotion, theme, body, symbol, pattern"
      >
        {[0.35, 0.6, 0.85, 1].map((t) => (
          <circle key={t} className="ostat-radar-ring" cx={cx} cy={cy} r={R * t} />
        ))}
        {RADAR_AXES.map((_, i) => {
          const a = -Math.PI / 2 + (i / RADAR_AXES.length) * Math.PI * 2;
          return (
            <line
              key={i}
              className="ostat-radar-spoke"
              x1={cx}
              y1={cy}
              x2={cx + Math.cos(a) * R}
              y2={cy + Math.sin(a) * R}
            />
          );
        })}

        <path className="ostat-radar-blob was" d={wasPath} />
        <path className="ostat-radar-blob now" d={nowPath} />

        {RADAR_AXES.map((label, i) => {
          const a = -Math.PI / 2 + (i / RADAR_AXES.length) * Math.PI * 2;
          const lx = cx + Math.cos(a) * (R + 22);
          const ly = cy + Math.sin(a) * (R + 22);
          return (
            <text
              key={label}
              className={`ostat-radar-axis${active === i ? ' is-hot' : ''}`}
              x={lx}
              y={ly}
              textAnchor="middle"
              dominantBaseline="middle"
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
            >
              {label}
            </text>
          );
        })}
      </svg>
      <div className="ostat-radar-legend">
        <span>
          <i className="ostat-radar-swatch was" /> Quieter window
        </span>
        <span>
          <i className="ostat-radar-swatch now" /> Rising now
        </span>
        {active != null ? (
          <span className="ostat-radar-focus">
            {RADAR_AXES[active]} · {Math.round(now[active] * 100)}%
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Derive mandala label/% from weather + rising emotion signals. */
export function presenceFromMood(mood: MoodWeather | null, todayMood: MoodKey | null): {
  label: string;
  percent: number;
  subtitle: string;
} {
  if (!mood) {
    return { label: 'Quiet', percent: 12, subtitle: 'Waiting for your first check-in and entries.' };
  }
  const emo = mood.risingSignals.find((s) => s.kind === 'emotion');
  if (emo) {
    const total = mood.risingSignals.reduce((a, s) => a + s.count, 0) || 1;
    return {
      label: emo.label,
      percent: Math.round((emo.count / total) * 100),
      subtitle: 'Strongest emotion signal Oreia is holding from your writing.',
    };
  }
  if (todayMood) {
    return {
      label: moodWord(todayMood, 'Steady'),
      percent: 48,
      subtitle: 'From today’s check-in — journals will refine this as you write.',
    };
  }
  if (mood.journalNetValence != null) {
    const n = valenceNorm(mood.journalNetValence) ?? 0.5;
    const label = n < 0.35 ? 'Heavy' : n < 0.55 ? 'Mixed' : n < 0.75 ? 'Steady' : 'Bright';
    return {
      label,
      percent: Math.round(n * 100),
      subtitle: 'Tone inferred from recent journal emotion signals.',
    };
  }
  return { label: 'Emerging', percent: 20, subtitle: 'A few more entries and the presence sharpens.' };
}
